import React from 'react'
import styled, { css } from 'styled-components'
import orderBy from 'lodash/orderBy'
import ReactList from 'react-list'

import { SPACING as constants } from '../../constants'

import moment from '../../lib/moment'
import { createTable } from '../../lib/components/Table'
import Tooltip, { withTooltip } from '../../lib/components/Tooltip'
import { parseFinancial } from '../../lib/utils'

import { PROJECT_TYPE, PROJECT_STATUS } from '../../pages/project-profit-sheet/constants'

import arrow_down from '../../static/images/arrow-down.svg'
import arrow_up from '../../static/images/arrow-up.svg'

// prettier-ignore
const BaseTable = createTable(css`
  grid-template-columns:
     120px
     minmax(170px, 1fr)
     120px
     120px
     150px
     120px
     115px
     115px
     130px
     80px
     90px;
  justify-content: start;

  > :first-child {
    padding-left: ${constants.GAP};
  }

  > :last-child {
    padding-right: ${constants.GAP};
  }
`)

const Table = {
  ...BaseTable,
  RowCellTooltip: styled(BaseTable.RowCell)`
    ${withTooltip}

    ${Tooltip} {
      top: 80%;
    }

    &:hover ${Tooltip} {
      transition-delay: 1s;
    }
  `,
}

const Layout = {
  ScrollWrapper: styled.div`
    height: calc(100% - 60px);
    overflow: scroll;
  `,
}

const Icon = styled.img`
  height: 10px;
  margin-left: 5%;
  cursor: pointer;
`

/**
 * @typedef {object} Props
 * @property {Array} data
 */

/**
 * @typedef {import('react-router').RouteComponentProps} RouteComponentProps
 * @param {Props & RouteComponentProps} props
 */
function ProjectListTable({ data, match, search, sort, type, lob, status, handleClickSort, history }) {
  /**
   * @param {string} id
   * @returns {import('history').LocationDescriptorObject}
   */
  const urlWithHeader = id => ({
    pathname: `${match.url}/${id}`,
    state: { header: 'Project Profit Sheet', id },
  })

  const arrow = column => {
    if (column === sort.column) {
      return sort.order ? <Icon src={ arrow_down } alt="logo" /> : <Icon src={ arrow_up } alt="logo" />
    }
    return null
  }

  const filterData = () => {
    const s = search.toLowerCase()
    let filteredData = data

    if (type !== '') {
      filteredData = filteredData.filter(f => f.project_type === type)
    }
    if (lob !== '') {
      filteredData = filteredData.filter(f => f.lob === lob)
    }
    if (status !== '') {
      filteredData = filteredData.filter(f => f.project_status === status)
    }

    return filteredData.filter(d => {
      const matchCode = d.project_code && d.project_code.toLowerCase().includes(s)
      const matchName = d.project_name && d.project_name.toLowerCase().includes(s)
      const matchType = d.project_type && d.project_type.toLowerCase().includes(s)
      const matchLOB = d.lob && d.lob.toLowerCase().includes(s)
      const matchKeyPM = d.key_project_manager && d.key_project_manager.toLowerCase().includes(s)

      return matchCode || matchName || matchType || matchLOB || matchKeyPM
    })
  }

  const sortData = () => {
    const list = filterData()
    const order = [
      'project_code',
      'project_name',
      'project_type',
      'lob',
      'project_status',
      data => {
        return moment(data.start_date, 'DD/MM/YYYY').toDate()
      },
      data => {
        return moment(data.end_date, 'DD/MM/YYYY').toDate()
      },
      data => {
        const $ = data.revenue
        if ($ === '-') return sort.order ? -Infinity : Infinity
        return parseFinancial($)
      },
      data => {
        const ratio = data.gross_profit_service_ratio
        return ratio || (sort.order ? -Infinity : Infinity)
      },
      data => {
        const submit = data.latest_submit
        if (submit === '-') return sort.order ? -Infinity : Infinity
        return moment(submit, 'MM/YYYY').toDate()
      },
      'key_project_manager',
    ]
    return orderBy(list, order[sort.column], sort.order ? 'desc' : 'asc')
  }

  const renderdata = sortData()

  const renderItem = (idx, key) => {
    return (
      <Table.Row key={key} onClick={() => history.push(urlWithHeader(renderdata[idx].project_id))}>
        <Table.RowCellTooltip currency style={ { justifyContent: 'flex-start' } }>
          <Table.Ellipsis>{renderdata[idx].project_code}</Table.Ellipsis>
          <Tooltip>{renderdata[idx].project_code}</Tooltip>
        </Table.RowCellTooltip>
        <Table.RowCellTooltip>
          <Table.Ellipsis>{renderdata[idx].project_name}</Table.Ellipsis>
          <Tooltip>{renderdata[idx].project_name}</Tooltip>
        </Table.RowCellTooltip>
        <Table.RowCell>{PROJECT_TYPE[renderdata[idx].project_type]}</Table.RowCell>
        <Table.RowCell>{renderdata[idx].lob}</Table.RowCell>
        <Table.RowCell>
          {renderdata[idx].key_project_manager ? renderdata[idx].key_project_manager : '-'}
        </Table.RowCell>
        <Table.RowCell>{PROJECT_STATUS[renderdata[idx].project_status]}</Table.RowCell>
        <Table.RowCell>{renderdata[idx].start_date}</Table.RowCell>
        <Table.RowCell>{renderdata[idx].end_date}</Table.RowCell>
        <Table.RowCell currency>{renderdata[idx].revenue}</Table.RowCell>
        <Table.RowCell currency>
          {renderdata[idx].gross_profit_ratio && renderdata[idx].gross_profit_ratio !== '-' ? `${renderdata[idx].gross_profit_ratio}%` : '-'}
        </Table.RowCell>
        <Table.RowCell currency>{renderdata[idx].latest_submit}</Table.RowCell>
      </Table.Row>
    )
  }

  return (
    <Layout.ScrollWrapper>
      <Table.Head>
        <Table.HeadCell onClick={ () => handleClickSort(0) }>
          Proj. code
          {arrow(0)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(1) }>
          Project Name
          {arrow(1)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(2) }>
          Project type
          {arrow(2)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(3) }>
          LOB
          {arrow(3)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(10) }>
          Key PM
          {arrow(10)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(4) }>
          Status
          {arrow(4)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(5) }>
          Start date
          {arrow(5)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(6) }>
          End date
          {arrow(6)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(7) }>
          Revenue
          {arrow(7)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(8) }>
          All GP Ratio
          {arrow(8)}
        </Table.HeadCell>
        <Table.HeadCell onClick={ () => handleClickSort(9) }>
          Lastest submit
          {arrow(9)}
        </Table.HeadCell>
      </Table.Head>
      <ReactList
        type="uniform"
        itemRenderer={ renderItem }
        length={ renderdata.length }
      />
    </Layout.ScrollWrapper>
  )
}

export default ProjectListTable



// WEBPACK FOOTER //
// ./src/components/project-profit-sheet/ProjectListTable.jsx